/*
 * <anzu-sequence> — scroll-scrubbed render sequence for the Anzu A+ story.
 *
 * A tall track pins a square canvas; scroll progress across the track drives a timeline of
 * segments, so several renders play as one continuous parallax under the reader's thumb.
 *
 * Segments come from the Liquid as a plan (data-seq-plan), each weighted in vh:
 *   {k:'s'} scrub  — map the segment onto a chapter's frame range
 *   {k:'l'} slide  — hold the outgoing chapter's last frame as a fixed backdrop while the
 *                    incoming chapter rises over it, top-first, like a lift
 * Slides are mapped linearly: easing scrub-driven motion breaks the 1:1 tie between thumb
 * travel and screen travel.
 *
 * Frames are held as HTMLImageElement, never ImageBitmap: 198 decoded 1024² surfaces would
 * pin ~500MB and OOM mobile Safari, so we let the engine evict decodes as it needs. Loading
 * is deferred until the section is within ~2 viewports, runs stride-first so a partial load
 * already covers the whole arc, and falls back to the nearest loaded neighbour rather than
 * blanking when a frame hasn't landed yet.
 *
 * The stage sits at top:0, under the theme's sticky header, so everything is drawn inside a
 * safe box below that header: the later sensor frames leave only ~1% headroom inside their
 * own square and would otherwise be clipped along the top.
 *
 * Text beats (data-seq-beats, mobile only) ride the same progress value. Each owns a window
 * inside ONE chapter's scrub, declared as a percentage of that chapter, so the copy stays
 * pinned to its frames when Scroll distance changes. Inside its window a beat travels
 * LINEARLY and fades on a smoothstep: parallax is a constant velocity difference, and easing
 * the travel would read as lag rather than depth — the same reason slides are mapped
 * linearly above.
 *
 * Layout is measured, not guessed, in both directions:
 *
 *   - Copy is anchored to an EDGE of the safe box by a margin, never offset from its centre.
 *     The block's own height therefore never enters the sum, so a heading that wraps to a
 *     second line grows away from the edge instead of sliding off it.
 *   - The render is handed whatever the copy leaves. computeFitBands() reads each block's real
 *     height and derives one band per chapter, so the band tightens when the copy grows.
 *   - Inside that band the SUBJECT is fitted, not its square, using per-frame bounding boxes
 *     measured from the WebP files (snippets/anzu-aplus-insets.liquid). The hero subject
 *     swings from 27.7% to 77.3% of its own frame across the rotation, so scaling the square
 *     — by any single number — cannot hold the mouse still. Fitting the subject and dropping
 *     its centre on the band's centre gives one fixed centre and a near-constant size.
 *
 * A beat may still override the framing with its own band/scale keyframes; those are grouped
 * into one track PER CHAPTER and never read outside it, so an offset cannot leak onward. The
 * slide inherits the outgoing chapter's framing for its backdrop and paints the incoming
 * panel neutral, so nothing jumps at the handover.
 *
 * Reduced motion collapses the track to a single viewport and draws one still frame.
 */
(function () {
  var MAX_DPR = 2; // 3x on a 1024px source buys nothing but fill-rate
  var CONCURRENCY = 6;
  var SIDE_FILL = 0.7; // default share of the stage width a subject may span
  var GAP_MIN = 22, GAP_MAX = 44, GAP_FRAC = 0.05; // breathing room between copy and render

  function reduceMotion() {
    return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // [top, bottom] as fractions of the safe box, falling back to the whole box.
  function normBand(top, bottom) {
    var t = (top == null ? 0 : top) / 100;
    var b = (bottom == null ? 100 : bottom) / 100;
    return b > t ? [t, b] : [0, 1];
  }

  // The theme writes these on <html> from theme.js as the sticky header resizes.
  function headerHeight() {
    var cs = getComputedStyle(document.documentElement);
    var v = parseFloat(cs.getPropertyValue('--sticky-header-height'));
    if (!v) v = parseFloat(cs.getPropertyValue('--header-height'));
    return v > 0 ? v : 0;
  }

  // <mobile-dock> writes this on <html> as it measures itself. On a PDP the dock carries the
  // price and Add to cart and sits over the bottom of every fixed stage, so anything drawn
  // under it is simply invisible — the safe box has to end above it, not at the viewport edge.
  function dockHeight() {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mobile-dock-height'));
    return v > 0 ? v : 0;
  }

  class AnzuSequence extends HTMLElement {
    connectedCallback() {
      if (this.dataset.seqReady) return;
      this.dataset.seqReady = '1';

      this.track = this.querySelector('[data-seq-track]');
      this.stage = this.querySelector('[data-seq-stage]');
      this.canvas = this.querySelector('[data-seq-canvas]');
      var data = this.querySelector('[data-seq-frames]');
      var planEl = this.querySelector('[data-seq-plan]');
      if (!this.track || !this.stage || !this.canvas || !data) return;

      try {
        this.urls = JSON.parse(data.textContent);
      } catch (e) {
        this.urls = [];
      }
      if (!this.urls.length || !this.canvas.getContext) return;

      this.ctx = this.canvas.getContext('2d', { alpha: false });
      if (!this.ctx) return;

      this.count = this.urls.length;
      this.bg = this.dataset.bg || '#000000';
      this.still = Math.min(this.count - 1, Math.max(0, (parseInt(this.dataset.still, 10) || 1) - 1));
      this.images = new Array(this.count);
      this.loaded = new Uint8Array(this.count);
      this.queue = [];
      this.pending = 0;
      this.pos = null; // key of what's currently painted — cheap redraw guard
      this.lastP = 0;
      this.current = -1;
      this.ticking = false;
      this.active = false;
      this.painted = false;
      this.safe = 0;
      this.safeB = 0;
      this.safeCss = 0; // header/dock heights in CSS px — cached so the scroll path never reads layout
      this.safeBCss = 0;
      this.stageH = 0;

      this.buildPlan(planEl);

      // The beat layer runs at every width: the composition is expressed in fractions of the
      // safe box, so desktop needs no second set of numbers. Only reduced motion opts out.
      this.mobile = true;
      this.buildInsets(this.querySelector('[data-seq-insets]'));
      this.beatEls = Array.prototype.slice.call(this.querySelectorAll('[data-seq-beat]'));
      this.buildBeats(this.querySelector('[data-seq-beats]'));

      // Diagnostics: add ?seqdebug to the URL to see what the runtime actually measured on the
      // device. Gated on the query string, so it can never render for a shopper.
      if (location.search.indexOf('seqdebug') > -1) {
        this.dbg = document.createElement('pre');
        this.dbg.setAttribute('style',
          'position:fixed;left:0;top:0;z-index:99999;margin:0;padding:6px 8px;' +
          'font:10px/1.35 monospace;color:#0f0;background:rgba(0,0,0,.85);white-space:pre;' +
          'pointer-events:none;max-width:60vw');
        document.body.appendChild(this.dbg);
      }

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.render = this.render.bind(this);

      this.static = reduceMotion();
      if (this.static) this.classList.add('is-static');

      this.resize();
      this.observeBoxes();
      // Webfonts land after first layout and change every block's height, which moves the
      // band the render is fitted into.
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        var self = this;
        document.fonts.ready.then(function () {
          self.computeFitBands();
          self.pos = null;
          self.paintAt(self.lastP);
          self.updateBeats(self.lastP);
        });
      }
      this.observe();
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
    }

    // The safe box depends on three things that all settle AFTER this element connects: the
    // stage's own height, the sticky header, and <mobile-dock>, which measures itself and only
    // reveals once the reader has scrolled. Caching any of them once is how the render ends up
    // sized against a box ~a dock taller than the one the copy is laid out in.
    observeBoxes() {
      if (typeof ResizeObserver === 'undefined') return;
      var self = this;
      this.ro = new ResizeObserver(function () { self.onResize(); });
      this.ro.observe(this.stage);
      var dock = document.querySelector('mobile-dock');
      if (dock) this.ro.observe(dock);
      var head = document.querySelector('sticky-header, [data-sticky-header], .section-header');
      if (head) this.ro.observe(head);
    }

    disconnectedCallback() {
      this.setActive(false);
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      if (this.pre) this.pre.disconnect();
      if (this.vis) this.vis.disconnect();
    }

    /* ---- timeline ---- */

    // Segments arrive weighted in vh; they're normalised to cumulative progress here so the
    // Liquid never needs float division (divided_by floors when both operands are ints).
    buildPlan(planEl) {
      var plan = [];
      if (planEl) {
        try { plan = JSON.parse(planEl.textContent) || []; } catch (e) { plan = []; }
      }
      if (!plan.length) plan = [{ k: 's', a: 0, b: this.count - 1, sc: 100, vh: 100 }];

      var i, total = 0;
      for (i = 0; i < plan.length; i++) total += Math.max(0, plan[i].vh || 0);
      if (total <= 0) total = 1;

      var acc = 0;
      // Each chapter owns a fixed band of the safe box: the render sits in one place for the
      // whole chapter and only the frames change. A slide carries the OUTGOING chapter's band
      // so its held backdrop stays exactly where the scrub left it.
      var lastBand = null, lastFit = 0, lastCi = -1, lastWf = null, ci = 0;
      for (i = 0; i < plan.length; i++) {
        plan[i].w = Math.max(0, plan[i].vh || 0) / total;
        plan[i].c = acc;
        acc += plan[i].w;
        if (plan[i].k === 's') {
          plan[i].ci = ci++;
          plan[i].band = normBand(plan[i].bt, plan[i].bb);
          lastBand = plan[i].band; lastFit = plan[i].ft || 0; lastCi = plan[i].ci; lastWf = plan[i].wf;
        } else {
          plan[i].pband = lastBand;
          plan[i].pfit = lastFit;
          plan[i].pwf = lastWf;
          plan[i].pci = lastCi;
        }
      }
      // A slide has to draw the incoming chapter exactly as that chapter will draw itself,
      // or the panel jumps to a different size the instant the scrub takes over.
      for (i = 0; i < plan.length; i++) {
        if (plan[i].k !== 'l') continue;
        var nxt = plan[i + 1];
        if (!nxt || nxt.k !== 's') continue;
        plan[i].nci = nxt.ci;
        plan[i].nfit = nxt.ft || 0;
        plan[i].nwf = nxt.wf;
        plan[i].nband = nxt.band;
      }
      this.plan = plan;
    }

    segmentAt(p) {
      var plan = this.plan;
      for (var i = plan.length - 1; i >= 0; i--) {
        if (p >= plan[i].c || i === 0) {
          var lp = plan[i].w > 0 ? (p - plan[i].c) / plan[i].w : 1;
          return { seg: plan[i], lp: lp < 0 ? 0 : lp > 1 ? 1 : lp };
        }
      }
      return { seg: plan[0], lp: 0 };
    }

    // Where the subject actually sits inside each frame, as per-mille of the square. Without
    // this the runtime can only centre-and-scale the whole square, which is why a mouse that
    // fills 28% of one frame and 77% of the next could never hold a steady size.
    buildInsets(el) {
      this.insets = null;
      if (!el) return;
      var chapters;
      try { chapters = JSON.parse(el.textContent) || []; } catch (e) { return; }
      var flat = [];
      for (var i = 0; i < chapters.length; i++) {
        if (chapters[i] && chapters[i].length) flat = flat.concat(chapters[i]);
      }
      // Anything short of one complete box per frame is unusable — fall back to square fitting.
      if (flat.length === this.count * 4) this.insets = flat;
    }

    // [x0, y0, x1, y1] as fractions of the square, or null.
    inset(i) {
      var d = this.insets;
      if (!d) return null;
      var k = i * 4;
      return [d[k] / 1000, d[k + 1] / 1000, d[k + 2] / 1000, d[k + 3] / 1000];
    }

    /* ---- text beats ---- */

    // Beats arrive chapter-relative: {ch, s, e} where s/e are percentages of chapter `ch`'s
    // scrub. Resolving them against the normalised plan here is what keeps the copy tied to
    // its frames when the merchant retunes Scroll distance or adds a chapter.
    buildBeats(dataEl) {
      var raw = [];
      if (dataEl) {
        try { raw = JSON.parse(dataEl.textContent) || []; } catch (e) { raw = []; }
      }

      var scrubs = [];
      var i;
      for (i = 0; i < this.plan.length; i++) {
        if (this.plan[i].k === 's') scrubs.push(this.plan[i]);
      }

      var beats = [];
      var tracks = {}; // chapter index -> its own render-offset track
      for (i = 0; i < raw.length; i++) {
        var b = raw[i];
        var seg = scrubs[b.ch] || scrubs[0];
        var el = this.beatEls[i];
        if (!seg || !el) continue;

        var s = seg.c + seg.w * ((b.s || 0) / 100);
        var e = seg.c + seg.w * ((b.e || 0) / 100);
        if (!(e > s)) continue;

        var end = seg.c + seg.w;
        var hold = !!b.h;
        // Held copy is cleared by the NEXT chapter rising over it, not by fading. Find the
        // slide that follows this beat's chapter: that span is when it is progressively
        // covered, and past it the copy is simply done. A chapter with no slide after it (the
        // last one) holds its copy to the end.
        var slide = null;
        for (var q = 0; q < this.plan.length; q++) {
          if (this.plan[q] === seg && this.plan[q + 1] && this.plan[q + 1].k === 'l') {
            slide = this.plan[q + 1];
            break;
          }
        }
        var holdUntil = slide ? slide.c + slide.w : 1;
        // The slide immediately BEFORE this chapter carries the copy in: the panel rises with
        // its own heading and caption already on it, rather than them fading in afterwards.
        var ride = null;
        for (q = 0; q < this.plan.length; q++) {
          if (this.plan[q] === seg && q > 0 && this.plan[q - 1].k === 'l' && Math.abs(s - seg.c) < 1e-6) {
            ride = this.plan[q - 1];
            break;
          }
        }

        beats.push({
          el: el,
          s: s,
          e: e,
          span: e - s,
          ch: b.ch,
          an: b.an ? 1 : 0,         // 0 = top edge, 1 = bottom edge
          m0: b.m0 == null ? 6 : b.m0,
          m1: b.m1 == null ? 6 : b.m1,
          // A beat carried in on the rising panel arrives already drawn, so it must not fade
          // in again the instant the panel seats — that reads as a blink at the handover.
          fi: ride ? 0 : Math.max(0, Math.min(0.9, (b.fi || 0) / 100)),
          // A held beat never fades out — it is covered by the next chapter instead.
          fo: hold ? 0 : Math.max(0, Math.min(0.9, (b.fo || 0) / 100)),
          hold: hold,
          holdUntil: holdUntil,
          cover: slide,   // the slide that covers this copy on the way out
          ride: ride,     // the slide that carries this copy in
          h: 0,
          o: -1,   // last written values; -1/null force the first write
          y: null
        });

        if (!b.r) continue;
        // The band may travel too: the render's box can slide from the top half to the bottom
        // half while the copy crosses it the other way. An inverted band would collapse the
        // render to nothing, so it falls back to the full safe box.
        var band0 = normBand(b.bt, b.bb);
        var band1 = normBand(b.bt2 == null ? b.bt : b.bt2, b.bb2 == null ? b.bb : b.bb2);
        // Keys are grouped per chapter and never consulted outside it. That scoping — rather
        // than ramping back to neutral before the chapter ends — is what stops an offset
        // leaking onward: a beat that holds to the very last frame has to keep its
        // composition, because that frame is what the reader sits on during the handover.
        var trk = tracks[b.ch];
        if (!trk) trk = tracks[b.ch] = { a: seg.c, b: end, keys: [] };
        trk.keys.push({
          p: s,
          y: (b.ry0 || 0) / 100, sc: (b.rs0 == null ? 100 : b.rs0) / 100,
          t0: band0[0], t1: band0[1]
        });
        trk.keys.push({
          p: e,
          y: (b.ry1 || 0) / 100, sc: (b.rs1 == null ? 100 : b.rs1) / 100,
          t0: band1[0], t1: band1[1]
        });
      }

      var rtracks = [];
      for (var ck in tracks) {
        if (!Object.prototype.hasOwnProperty.call(tracks, ck)) continue;
        var t = tracks[ck];
        t.keys.sort(function (x, y) { return x.p - y.p; });
        var dedup = [];
        for (i = 0; i < t.keys.length; i++) {
          var prev = dedup[dedup.length - 1];
          if (prev && Math.abs(prev.p - t.keys[i].p) < 1e-9) dedup[dedup.length - 1] = t.keys[i];
          else dedup.push(t.keys[i]);
        }
        t.keys = dedup;
        if (dedup.length) rtracks.push(t);
      }
      rtracks.sort(function (x, y) { return x.a - y.a; });

      this.beats = beats;
      this.rtracks = rtracks;
    }

    // Render offset at progress p: {y} as a share of the safe box, {sc} as a scale multiplier.
    // null means "leave the render exactly where it has always been" — desktop, or no beat
    // ever asked for a counter-move.
    renderAt(p) {
      var tr = this.rtracks;
      if (!this.mobile || !tr || !tr.length) return null;
      for (var n = 0; n < tr.length; n++) {
        // Outside a driving chapter the render is left exactly where it has always been.
        if (p < tr[n].a - 1e-9 || p > tr[n].b + 1e-9) continue;
        var k = tr[n].keys;
        if (p <= k[0].p) return k[0];
        var last = k[k.length - 1];
        if (p >= last.p) return last;
        for (var i = 1; i < k.length; i++) {
          if (p > k[i].p) continue;
          var a = k[i - 1];
          var b = k[i];
          var d = b.p - a.p;
          var t = d > 0 ? (p - a.p) / d : 0;
          return {
            y: a.y + (b.y - a.y) * t,
            sc: a.sc + (b.sc - a.sc) * t,
            t0: a.t0 + (b.t0 - a.t0) * t,
            t1: a.t1 + (b.t1 - a.t1) * t
          };
        }
        return last;
      }
      return null;
    }

    // Runs on every frame, outside paintAt's redraw guard: a beat keeps travelling while the
    // frame index sits still, so gating this on "did the frame change" would stutter the copy.
    updateBeats(p) {
      var beats = this.beats;
      if (!beats || !beats.length || !this.mobile || this.static) return;
      var avail = Math.max(1, this.stageH - this.safeCss - this.safeBCss);
      var at = this.segmentAt(p);
      var seg = at.seg;
      // Through a handover the panel's top edge sweeps up the stage. Copy on either side of it
      // moves WITH it rather than cross-fading: the outgoing copy is progressively covered, the
      // incoming copy rides up already in place. Both are the same edge, read from paintSlide.
      var sliding = seg.k === 'l';
      var rise = sliding ? (1 - at.lp) * this.stageH : 0;
      var edge = rise - this.safeCss; // that edge in the beat layer's own coordinates

      for (var i = 0; i < beats.length; i++) {
        var b = beats[i];
        var o;
        var y;
        var clip = 0;

        if (sliding && b.ride === seg) {
          // Carried in on the panel: fully drawn, offset by however far the panel still has to go.
          o = 1;
          y = (b.m0 / 100) * avail;
          y = b.an ? y - rise : y + rise;
        } else if (p < b.s) {
          // p is already clamped to [0,1], so there is no "before the start" for a beat that
          // opens the section — it simply reads as fully arrived.
          o = 0;
          y = (b.m0 / 100) * avail;
        } else if (p > b.e) {
          // Held copy parks at the end of its travel and stays lit until the next chapter has
          // finished rising over it — after that it is off, so it cannot reappear above copy
          // belonging to a later chapter.
          o = b.hold && p <= b.holdUntil + 1e-6 ? 1 : 0;
          y = (b.m1 / 100) * avail;
        } else {
          var t = b.span > 0 ? (p - b.s) / b.span : 1;
          o = 1;
          if (b.fi > 0 && t < b.fi) o = t / b.fi;
          else if (b.fo > 0 && t > 1 - b.fo) o = (1 - t) / b.fo;
          o = o * o * (3 - 2 * o); // smoothstep the alpha only
          y = ((b.m0 + (b.m1 - b.m0) * t) / 100) * avail; // linear travel — see the header note
        }

        // Outgoing held copy is covered by the rising panel, never faded. Clipping per beat
        // rather than across the whole layer is what lets the incoming copy ride up in the
        // same frame without being clipped away by the outgoing copy's mask.
        if (sliding && b.cover === seg && o > 0 && b.h > 0) {
          var top = b.an ? avail - y - b.h : y;
          var shown = Math.max(0, Math.min(b.h, edge - top));
          clip = b.h - shown;
        }

        var oq = Math.round(o * 100) / 100;
        var yq = Math.round(y);
        var cq = Math.round(clip);
        if (oq !== b.o) { b.o = oq; b.el.style.setProperty('--b-o', oq); }
        if (yq !== b.y) { b.y = yq; b.el.style.setProperty('--b-y', yq + 'px'); }
        if (cq !== b.c) { b.c = cq; b.el.style.setProperty('--b-clip', cq + 'px'); }
      }
    }

    // Render offset at progress p: {y} as a share of the safe box, {sc} as a scale multiplier.
    // null means "leave the render exactly where it has always been" — desktop, or no beat
    // ever asked for a counter-move.
    renderAt(p) {
      var tr = this.rtracks;
      if (!this.mobile || !tr || !tr.length) return null;
      for (var n = 0; n < tr.length; n++) {
        // Outside a driving chapter the render is left exactly where it has always been.
        if (p < tr[n].a - 1e-9 || p > tr[n].b + 1e-9) continue;
        var k = tr[n].keys;
        if (p <= k[0].p) return k[0];
        var last = k[k.length - 1];
        if (p >= last.p) return last;
        for (var i = 1; i < k.length; i++) {
          if (p > k[i].p) continue;
          var a = k[i - 1];
          var b = k[i];
          var d = b.p - a.p;
          var t = d > 0 ? (p - a.p) / d : 0;
          return {
            y: a.y + (b.y - a.y) * t,
            sc: a.sc + (b.sc - a.sc) * t,
            t0: a.t0 + (b.t0 - a.t0) * t,
            t1: a.t1 + (b.t1 - a.t1) * t
          };
        }
        return last;
      }
      return null;
    }

    // Runs on every frame, outside paintAt's redraw guard: a beat keeps travelling while the
    // frame index sits still, so gating this on "did the frame change" would stutter the copy.
    // The render gets the space the copy does not want. Derived once per chapter from the
    // beats' anchors and their MEASURED heights, so it holds still while copy fades in and out
    // — and so a heading that wraps to a second line pushes the render down instead of
    // landing on top of it.
    computeFitBands() {
      this.fitBands = null;
      this.bandsMeasured = false;
      if (!this.beats || !this.beats.length) return;
      var avail = Math.max(1, this.stageH - this.safeCss - this.safeBCss);
      var gap = Math.min(GAP_MAX, Math.max(GAP_MIN, avail * GAP_FRAC));
      var bands = {};
      var i;
      for (i = 0; i < this.beats.length; i++) {
        var b = this.beats[i];
        if (!b.el) continue;
        var h = b.el.offsetHeight || 0;
        b.h = h;
        if (h > 0) this.bandsMeasured = true;
        // The extreme of the beat's travel is what has to be cleared, not its midpoint.
        var m = (Math.max(b.m0, b.m1) / 100) * avail;
        var band = bands[b.ch] || (bands[b.ch] = { top: 0, bot: avail });
        if (b.an) band.bot = Math.min(band.bot, avail - m - h);
        else band.top = Math.max(band.top, m + h);
      }
      for (var k in bands) {
        if (!Object.prototype.hasOwnProperty.call(bands, k)) continue;
        var t0 = (bands[k].top + gap) / avail;
        var t1 = (bands[k].bot - gap) / avail;
        // If the copy has eaten the screen, fall back to the whole box rather than nothing.
        bands[k] = (t1 - t0) > 0.15 ? [t0, t1] : [0, 1];
      }
      this.fitBands = bands;
    }

    /* ---- visibility: defer bytes until the section is nearly in play ---- */

    observe() {
      var self = this;
      if (!('IntersectionObserver' in window)) {
        this.start();
        this.setActive(true);
        return;
      }
      this.pre = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        self.pre.disconnect();
        self.pre = null;
        self.start();
      }, { rootMargin: '200% 0px' });
      this.pre.observe(this);

      this.vis = new IntersectionObserver(function (entries) {
        self.setActive(entries[0].isIntersecting);
      }, { rootMargin: '10% 0px' });
      this.vis.observe(this.track);
    }

    setActive(on) {
      if (on === this.active || this.static) return;
      this.active = on;
      if (on) {
        // Coming into view is when content-visibility releases layout, and by now the dock has
        // measured itself, so re-read the whole box rather than just the band.
        this.resize();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      } else {
        window.removeEventListener('scroll', this.onScroll);
      }
    }

    /* ---- loading ---- */

    start() {
      if (this.static) {
        this.queue = [this.still];
      } else {
        // Coarse-to-fine: a half-finished load still spans the whole sequence.
        var order = [0];
        var seen = new Uint8Array(this.count);
        seen[0] = 1;
        for (var stride = 16; stride >= 1; stride = stride >> 1) {
          for (var i = 0; i < this.count; i += stride) {
            if (seen[i]) continue;
            seen[i] = 1;
            order.push(i);
          }
        }
        this.queue = order;
      }
      this.pump();
    }

    pump() {
      while (this.pending < CONCURRENCY && this.queue.length) {
        this.load(this.queue.shift());
      }
    }

    load(i) {
      if (this.images[i]) return;
      var self = this;
      var img = new Image();
      this.images[i] = img;
      this.pending++;
      img.decoding = 'async';
      img.onload = function () {
        self.pending--;
        self.loaded[i] = 1;
        self.repaint(); // a newer neighbour may now beat what's on screen
        self.pump();
      };
      img.onerror = function () {
        self.pending--;
        self.images[i] = null;
        self.pump();
      };
      img.src = this.urls[i];
    }

    nearest(i) {
      if (this.loaded[i]) return i;
      for (var d = 1; d < this.count; d++) {
        if (i - d >= 0 && this.loaded[i - d]) return i - d;
        if (i + d < this.count && this.loaded[i + d]) return i + d;
      }
      return -1;
    }

    /* ---- painting ---- */

    resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      var rect = this.stage.getBoundingClientRect();
      var w = Math.max(1, Math.round(rect.width * dpr));
      var h = Math.max(1, Math.round(rect.height * dpr));
      var hdr = headerHeight();
      var dock = dockHeight();
      var safe = Math.round(hdr * dpr);
      var safeB = Math.round(dock * dpr);
      var changed = this.canvas.width !== w || this.canvas.height !== h ||
        this.safe !== safe || this.safeB !== safeB;
      // Assigning width or height resets the canvas bitmap — always, even to the same value —
      // so whatever was on screen is gone. Retire the redraw guard here rather than at each
      // call site, or a caller that resizes without repainting leaves a blank stage.
      this.canvas.width = w;
      this.canvas.height = h;
      this.pos = null;
      this.safe = safe;
      this.safeB = safeB;
      // CSS-pixel twins for the beat layer, cached here so updateBeats never forces layout.
      this.safeCss = hdr;
      this.safeBCss = dock;
      this.stageH = rect.height;
      this.computeFitBands();
      return changed;
    }

    fill() {
      this.ctx.fillStyle = this.bg;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    // Draw frame `idx` fitted into a band of the safe box (below the sticky header), pushed
    // down by dy. `band` is [top, bottom] as fractions of the safe box and defaults to the
    // whole thing. Fitting to a band rather than scaling a centred square is what keeps the
    // composition honest across device heights: the copy above and below reserves its space
    // in the same units, so the render can never grow into it on a taller screen.
    paintFrame(idx, scale, dy, band, fitW) {
      var n = this.nearest(idx);
      if (n < 0) return false;
      var img = this.images[n];
      if (!img || !img.naturalWidth) return false;

      var w = this.canvas.width;
      var top = this.safe || 0;
      var availH = Math.max(1, this.canvas.height - top - (this.safeB || 0));
      var t0 = band ? band[0] : 0;
      var t1 = band ? band[1] : 1;
      var boxTop = top + availH * t0;
      var boxH = Math.max(1, availH * (t1 - t0));

      // Fit the SUBJECT rather than its square: size it to fill the box and drop its centre on
      // the box's centre. Because every frame's subject box was measured, the mouse holds one
      // apparent size and one centre through a rotation that swings it from 28% to 77% of its
      // own frame — which no single scale on the square could do.
      var ins = fitW ? this.inset(n) : null;
      if (ins) {
        var sw = Math.max(0.01, ins[2] - ins[0]);
        var sh = Math.max(0.01, ins[3] - ins[1]);
        var fitSide = Math.min((w * fitW) / sw, boxH / sh) * scale;
        this.ctx.drawImage(
          img,
          w / 2 - fitSide * ((ins[0] + ins[2]) / 2),
          boxTop + boxH / 2 - fitSide * ((ins[1] + ins[3]) / 2) + dy,
          fitSide, fitSide
        );
        this.current = n;
        return true;
      }

      var side = Math.min(w, boxH) * scale;
      this.ctx.drawImage(img, (w - side) / 2, boxTop + (boxH - side) / 2 + dy, side, side);
      this.current = n;
      return true;
    }

    // t = 1 fully below the fold, t = 0 seated. The incoming chapter is clipped to the
    // rising panel, so it reveals from its top edge downward like a lift.
    paintSlide(a, sa, b, sb, t, bro, pband, pfit, pauto, nband, nfit, nwf, nauto) {
      var w = this.canvas.width;
      var h = this.canvas.height;
      var y = t * h;
      // The backdrop is the outgoing chapter's last frame, so it keeps that chapter's
      // composition — held copy is still sitting on it. The rising panel belongs to the
      // incoming chapter and gets no offset at all.
      var avail = h - (this.safe || 0) - (this.safeB || 0);

      this.fill();
      this.paintFrame(
        a,
        sa * (bro ? bro.sc : 1),
        bro ? bro.y * avail : 0,
        bro ? [bro.t0, bro.t1] : (pauto || pband || null),
        pfit
      );

      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect(0, y, w, h - y);
      this.ctx.clip();
      this.ctx.fillStyle = this.bg;
      this.ctx.fillRect(0, y, w, h - y);
      var nf = nfit && this.insets ? ((nwf == null ? SIDE_FILL * 100 : nwf) / 100) : 0;
      this.paintFrame(b, sb, y, nauto || nband || null, nf);
      this.ctx.restore();
    }

    paintAt(p) {
      var at = this.segmentAt(p);
      var seg = at.seg;
      var key;
      var ok;

      if (seg.k === 'l') {
        var t = 1 - at.lp;
        key = 'l' + seg.a + '_' + Math.round(t * 500); // ~0.2% quantisation
        if (key === this.pos && this.painted) return;
        this.pos = key;
        // Sampled just inside the outgoing chapter so the backdrop matches the last scrubbed
        // frame exactly — no jump at the handover.
        var pf = seg.pfit && this.insets ? (seg.pwf == null ? SIDE_FILL * 100 : seg.pwf) / 100 : 0;
        this.paintSlide(seg.a, (seg.sa || 100) / 100, seg.b, (seg.sb || 100) / 100, t,
          this.renderAt(seg.c - 1e-9), seg.pband, pf,
          pf && this.fitBands ? this.fitBands[seg.pci] : null,
          seg.nband, seg.nfit, seg.nwf,
          seg.nfit && this.fitBands ? this.fitBands[seg.nci] : null);
        ok = true;
      } else {
        var i = seg.a + Math.round(at.lp * (seg.b - seg.a));
        // The counter-move can travel while the frame index holds, so it has to be part of
        // the redraw key — otherwise a sinking render freezes on a repeated frame.
        // A beat may still override the framing; otherwise the chapter fits its subject into
        // whatever the copy left, falling back to its declared band when there is no data.
        var ro = this.renderAt(p);
        var fitW = seg.ft && this.insets ? (seg.wf == null ? SIDE_FILL * 100 : seg.wf) / 100 : 0;
        var auto = fitW && this.fitBands ? this.fitBands[seg.ci] : null;
        var dy = ro ? ro.y * (this.canvas.height - (this.safe || 0) - (this.safeB || 0)) : 0;
        var sc = ro ? ro.sc : 1;
        var band = ro ? [ro.t0, ro.t1] : (auto || seg.band || null);
        key = 's' + i + '_' + Math.round(dy) + '_' + Math.round(sc * 200) +
          (band ? '_' + Math.round(band[0] * 200) + '_' + Math.round(band[1] * 200) : '');
        if (key === this.pos && this.painted) return;
        this.pos = key;
        this.fill();
        ok = this.paintFrame(i, ((seg.sc || 100) / 100) * sc, dy, band, fitW);
      }

      if (ok && !this.painted) {
        this.painted = true;
        this.classList.add('is-ready');
      }
    }

    repaint() {
      this.pos = null; // a frame landed; force the current position to redraw
      this.paintAt(this.lastP);
    }

    progress() {
      var rect = this.track.getBoundingClientRect();
      var span = rect.height - this.stage.offsetHeight;
      if (span <= 0) return 0;
      var p = -rect.top / span;
      return p < 0 ? 0 : p > 1 ? 1 : p;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(this.render);
    }

    render() {
      this.ticking = false;
      // The theme puts content-visibility:auto on below-fold sections, which suppresses layout
      // for everything inside them — so at connect time every beat measures 0 tall and the band
      // is computed as though there were no copy at all. Retry until the browser has actually
      // laid the copy out, then stop reading layout on the scroll path.
      // The safe box is not stable at connect time: the header resizes as it sticks, and the
      // dock measures itself only once the reader has scrolled. Both are cheap to re-read, and
      // this loop already reads layout for scroll progress, so verify rather than assume.
      if (!this.bandsMeasured || headerHeight() !== this.safeCss || dockHeight() !== this.safeBCss) {
        this.resize();
        this.pos = null;
      }
      this.lastP = this.static ? 0 : this.progress();
      this.paintAt(this.lastP);
      this.updateBeats(this.lastP);
      if (this.dbg) this.debugPanel(this.lastP);
    }

    // Prints the numbers the layout is actually derived from, so a mismatch between what the
    // model predicts and what the screen shows can be located instead of guessed at.
    debugPanel(p) {
      var avail = Math.max(1, this.stageH - this.safeCss - this.safeBCss);
      var at = this.segmentAt(p);
      var seg = at.seg;
      var band = this.fitBands ? this.fitBands[seg.ci] : null;
      var out = [
        'stage ' + Math.round(this.stageH) + '  header ' + Math.round(this.safeCss) +
          '  dock ' + Math.round(this.safeBCss) + '  box ' + Math.round(avail),
        'canvas ' + this.canvas.width + 'x' + this.canvas.height + '  dpr ' +
          (Math.min(window.devicePixelRatio || 1, 2)).toFixed(2),
        'insets ' + (this.insets ? 'loaded' : 'MISSING') + '   bandsMeasured ' + !!this.bandsMeasured +
          '   mobile ' + !!this.mobile,
        'seg k=' + seg.k + ' ci=' + seg.ci + ' ft=' + (seg.ft || 0) + ' wf=' + (seg.wf == null ? '-' : seg.wf),
        'band ' + (band ? Math.round(band[0] * avail) + '-' + Math.round(band[1] * avail) +
          ' (' + Math.round((band[1] - band[0]) * avail) + 'px)' : 'NONE -> falls back'),
        'p=' + p.toFixed(3) + '  frame ' + (this.current + 1)
      ];
      var ins = this.inset(this.current);
      if (ins && band) {
        var side = Math.min((this.canvas.width * ((seg.wf == null ? 70 : seg.wf) / 100)) / (ins[2] - ins[0]),
          ((band[1] - band[0]) * (this.canvas.height - this.safe - this.safeB)) / (ins[3] - ins[1]));
        var dpr = this.canvas.width / this.stage.getBoundingClientRect().width;
        out.push('subject ' + Math.round((ins[3] - ins[1]) * side / dpr) + 'px tall, ' +
          Math.round((ins[2] - ins[0]) * side / dpr) + 'px wide');
      }
      for (var i = 0; i < (this.beats || []).length; i++) {
        var b = this.beats[i];
        out.push('beat' + i + ' ' + (b.an ? 'bottom' : 'top   ') + ' h=' +
          (b.el ? b.el.offsetHeight : '?') + ' m=' + b.m0 + '/' + b.m1 + ' o=' + b.o + ' y=' + b.y);
      }
      this.dbg.textContent = out.join(String.fromCharCode(10));
    }

    onResize() {
      if (this.resize()) {
        this.pos = null;
        this.paintAt(this.lastP);
      }
      this.updateBeats(this.lastP);
    }
  }

  if (!customElements.get('anzu-sequence')) {
    customElements.define('anzu-sequence', AnzuSequence);
  }

  window.theme = window.theme || {};
  window.theme.AnzuSequence = AnzuSequence;
})();
